import type { Thread } from "@brookbush/database";
import { ContentType, type ThreadMessageRole } from "@brookbush/database";

import { z } from "zod";

// ─── Input Schemas ────────────────────────────────────────────────────────────

export const CreateThreadSchema = z.object({
	title: z.string().min(1).max(200),
	contentType: z.enum(ContentType).optional(),
	contentId: z.string().optional(),
});

export const UpdateThreadSchema = z.object({
	title: z.string().min(1).max(200),
});

export const SendMessageSchema = z.object({
	content: z.string().min(1).max(10000),
});

export const GetThreadsQuerySchema = z.object({
	cursor: z.coerce.number().optional(),
	limit: z.coerce.number().min(1).max(50).default(20),
});

export const CreateQuizAttemptSchema = z.object({
	quizId: z.string(),
	quizQuestionId: z.number(),
	selectedOption: z.string(),
	isCorrect: z.boolean(),
});

export const AddQuizAttemptQuestionSchema = z.object({
	quizQuestionId: z.number(),
	selectedOption: z.string(),
	isCorrect: z.boolean(),
});

// ─── Inferred Input Types ─────────────────────────────────────────────────────

export type CreateThreadInput = z.infer<typeof CreateThreadSchema> & {
	userId: string;
};

export type UpdateThreadInput = z.infer<typeof UpdateThreadSchema> & {
	threadId: number;
	userId: string;
};

export type ArchiveThreadInput = {
	threadId: number;
	userId: string;
};

export type UnarchiveThreadInput = {
	threadId: number;
	userId: string;
};

export type GetThreadInput = {
	threadId: number;
	userId: string;
};

export type GetThreadsInput = z.infer<typeof GetThreadsQuerySchema> & {
	userId: string;
};

export type GetQuizInput = {
	quizId: number;
	userId: string;
};

export type CreateMessageInput = z.infer<typeof SendMessageSchema> & {
	threadId: number;
	userId: string;
	role: ThreadMessageRole;
	metadata?: Record<string, unknown>;
};

export type CreateQuizAttemptInput = z.infer<typeof CreateQuizAttemptSchema> & {
	userId: string;
};

export type AddQuizAttemptQuestionInput = z.infer<
	typeof AddQuizAttemptQuestionSchema
>;

export type CheckRateLimitInput = {
	userId: string;
	isSubscriber: boolean;
};

export type CheckAndDecrementRateLimitInput = {
	userId: string;
	isSubscriber: boolean;
};

export type StreamMessageInput = {
	threadId: number;
	userId: string;
	messages: unknown[];
};

// ─── Response Types ───────────────────────────────────────────────────────────

export type GetThreadsResult = {
	threads: Thread[];
	nextCursor: number | null;
};

export type ChatRateLimitStatus = {
	isAllowed: boolean;
	remaining: number;
	resetAt: Date;
};

export type ThreadContent = {
	id: string;
	type: ContentType;
	title: string;
	slug: string;
};

export type RelatedContentRef = {
	title: string;
	slug?: string;
	type?: string;
};

export type ContentDetails = ThreadContent & {
	body: string;
	related: RelatedContentRef[];
};

export type ConnectedContentGrounding = {
	summary: ThreadContent;
	body: string;
};

// ─── RAG Types ────────────────────────────────────────────────────────────────

export type RetrieveContextInput = {
	query: string;
	contentId?: string;
	topK?: number;
};

export type ContextChunk = {
	contentType: string;
	contentId: string;
	slug: string;
	title: string;
	fragments: string[];
	score: number;
};
